import { Navigate, useNavigate } from "react-router"
import {
  ConsumptionPointFormValues,
  useStoredConsumptionPointFormValues,
} from "../consumptionPoint/consumptionPointForm.utils"
import { BusinessContactFormVales, useStoredBusinessContactFormValues } from "../contact/businessContactForm.utils"
import {
  HouseholdContactFormVales,
  useStoredHouseholdContactFormValues,
} from "../contact/householdContactFormController"
import { ContractTypeFormValues, useStoredContractTypeFormValues } from "../contractType/contractTypeForm.utils"
import { PaymentFormValues, useStoredPaymentFormValues } from "../payment/paymentForm.utils"
import {
  ProductionPointFormValues,
  useStoredProductionPointFormValues,
} from "../productionPoint/productionPointForm.utils"
import { useCustomerType, useProductParam } from "../utils"
import { ACQUISITION_FORM_LINKS } from "../links"
import { CenterWrapper } from "../components/CenterWrapper"
import { CustomerType } from "../../acquisitionForm/form/useAcquisitionFormSchema"
import { Spacer } from "../../../../../components/spacer/Spacer"
import { theme } from "../../../../../styles/stitches.config"
import { SummaryForm } from "./SummaryForm"
import { trpc } from "../../../../../api/trpc/trpc"
import { isTRPCError } from "../../../../../utils/types"
import { WEB_LINKS } from "../../links"
import { getContactInfo, getProductData } from "./summaryPage.utils"
import { pushAnalyticsEvent } from "../../../../../utils/analytics"
import { FormHeader } from "../components/FormHeader"
import { useAcquisitionProgress } from "../AcquisitionProgressContext"
import { AcquisitionFormStepProps } from "../acquisitionForm.config"

type ContentProps = {
  formVariant?: AcquisitionFormStepProps["formVariant"]
  customerType: CustomerType
  contractTypeFormValues: ContractTypeFormValues
  householdContactFormValues?: HouseholdContactFormVales
  businessContactFormValues?: BusinessContactFormVales
  consumptionPointFormValues?: ConsumptionPointFormValues
  productionPointFormValues?: ProductionPointFormValues
  paymentFormValues: PaymentFormValues
  clearStoredData?: () => void
}

function SummaryPageContent(props: ContentProps) {
  const { clearStoredData, ...formValues } = props

  const navigate = useNavigate()
  const submitMutation = trpc.useMutation("web.createCustomer")
  const progress = useAcquisitionProgress()

  function handleReturn() {
    navigate(`..${progress.previousStepUrl}`)
  }

  async function handleSubmit() {
    const contactInfo = getContactInfo(formValues)

    try {
      await submitMutation.mutateAsync({
        customer: {
          address: contactInfo.address,
          email: contactInfo.email,
          birthDate: contactInfo.birthDate,
          phone: contactInfo.phone,
          contactAddress: contactInfo.contactAddress ?? undefined,
          firstName: contactInfo.firstName,
          lastName: contactInfo.lastName,
          customerTypeData: contactInfo.customerTypeData,
          partnerCode: !!formValues.paymentFormValues.partnerCode
            ? formValues.paymentFormValues.partnerCode
            : undefined,
          productData: getProductData(props),
        },
        message: !!formValues.paymentFormValues.message ? formValues.paymentFormValues.message : undefined,
        formVariant: props.formVariant === "secondary" ? "D" : "C",
      })

      pushAnalyticsEvent("formSubmission", {
        formVariant: props.formVariant === "secondary" ? "D" : "C",
        formData: {
          ...formValues,
          form: "new",
        },
      })

      clearStoredData?.()
      navigate(`..${WEB_LINKS.acquisitionThankYou}`)
    } catch (error) {
      if (!isTRPCError(error)) {
        throw error
      }
    }
  }
  return (
    <CenterWrapper>
      <FormHeader>Souhrn</FormHeader>

      <Spacer size={theme.space.s6} />
      {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
      <SummaryForm {...formValues} onReturn={handleReturn} onSubmit={handleSubmit} />
    </CenterWrapper>
  )
}

export function SummaryPage(props: AcquisitionFormStepProps) {
  const customerType = useCustomerType()
  const product = useProductParam()
  const [contractType, , clearContractType] = useStoredContractTypeFormValues(customerType ?? "household")
  const [householdContactInfo, , clearHouseholdContactInfo] = useStoredHouseholdContactFormValues()
  const [businessContactInfo, , clearBusinessContactInfo] = useStoredBusinessContactFormValues()
  const [consumptionPointInfo, , clearConsumptionPointInfo] = useStoredConsumptionPointFormValues()
  const [productionPointInfo, , clearProductionPointInfo] = useStoredProductionPointFormValues(
    contractType?.product === "consumption" ? "production" : contractType?.product ?? "production"
  )

  const [paymentInfo, , clearPaymentInfo] = useStoredPaymentFormValues(contractType?.product ?? "production")

  if (!customerType) {
    return <Navigate to={`..${ACQUISITION_FORM_LINKS.CUSTOMER_TYPE}`} />
  }

  if (!contractType) {
    return <Navigate to={`..${ACQUISITION_FORM_LINKS.CONTRACT_TYPE(customerType)}`} />
  }

  if (!householdContactInfo && !businessContactInfo) {
    return <Navigate to={`..${ACQUISITION_FORM_LINKS.CONTACT(customerType, contractType.product)}`} />
  }

  if (contractType.product !== "production" && !consumptionPointInfo) {
    return <Navigate to={`..${ACQUISITION_FORM_LINKS.CONSUMPTION_POINT(customerType, contractType.product)}`} />
  }

  if (contractType.product !== "consumption" && !productionPointInfo) {
    return <Navigate to={`..${ACQUISITION_FORM_LINKS.PRODUCTION_POINT(customerType, contractType.product)}`} />
  }

  if (!paymentInfo) {
    return <Navigate to={`..${ACQUISITION_FORM_LINKS.PAYMENT(customerType, contractType.product)}`} />
  }

  if (contractType.product !== product) {
    return <Navigate to={`..${ACQUISITION_FORM_LINKS.SUMMARY(customerType, contractType.product)}`} />
  }

  function clearStoredData() {
    clearContractType()
    clearHouseholdContactInfo()
    clearBusinessContactInfo()
    clearConsumptionPointInfo()
    clearProductionPointInfo()
    clearPaymentInfo()
  }

  return (
    <SummaryPageContent
      customerType={customerType}
      contractTypeFormValues={contractType}
      householdContactFormValues={customerType === "household" ? householdContactInfo : undefined}
      businessContactFormValues={customerType === "business" ? businessContactInfo : undefined}
      consumptionPointFormValues={contractType.product !== "production" ? consumptionPointInfo : undefined}
      productionPointFormValues={contractType.product !== "consumption" ? productionPointInfo : undefined}
      paymentFormValues={paymentInfo}
      clearStoredData={clearStoredData}
      formVariant={props.formVariant}
    />
  )
}
